<template>
  <div class="container-fluid d-flex flex-column p-0 meek-color-dark-gray h-100">
    <div class="flex-grow-1 overflow-y-auto">
      <div class="d-flex flex-column min-h-100 justify-center-center">
        <meekijken-header useHeaderWithoutPayoff="true"></meekijken-header>
        <div class="container py-4">
          <div class="row justify-content-center">
            <div class="col-10 col-sm-8 col-md-6 col-lg-5">
              <h2 class="font-weight-normal text-center pb-2 mb-4 meek-color-dark-gray">{{ $t('Login.Login') }}</h2>
              <form method="post" @submit.prevent="validateLogin">
                <div>
                  <div class="form-group">
                    <label for="email" class="mb-0 font-weight-bold meek-color-dark-gray">{{ $t('Login.Username') }}</label>
                    <input spellcheck="false" type="email" class="form-control" id="email" name="email" ref="email" v-model="username" tabindex="1">
                  </div>
                  <div class="form-group mt-4">
                    <span class="float-right mb-0"><router-link :to="{ name: 'forgot_password'}" class="meek-color-dark-gray">{{ $t('Login.Forgot?') }}</router-link></span>
                    <label for="password" class="mb-0 font-weight-bold meek-color-dark-gray">{{ $t('Login.Password') }}</label>
                    <vue-password v-model="password"
                                  classes="form-control meek-password-input"
                                  id="password"
                                  ref="password"
                                  spellcheck="false"
                                  tabindex="2"
                                  disable-strength
                    />
                  </div>
                  <div class="form-row mb-2" v-show="showForceLogout">
                    {{ $t('Login.This user is already signed in on another location') }}
                  </div>
                  <div class="form-check mb-3" v-show="showForceLogout">
                    <input class="form-check-input" type="checkbox" value="1" id="logout_devices" v-model="forceLogout" tabindex="4">
                    <label class="form-check-label" for="logout_devices">
                      {{ $t('Login.Sign out on other devices') }}
                    </label>
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="!showError && $route.query.session_expired === 'true'">
                    {{ $t('Login.Session is expired') }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showError">
                    {{ $t('Login.Email address or password incorrect') }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="attemptsLeft">
                    {{ $t('Login.Only {ATTEMPTS} attempts left for this {REASON}', { ATTEMPTS: attemptsLeft + 1, REASON: typeOfBlock }) }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="attemptsLeft === 0">
                    {{ $t('Login.Last attempt before this {REASON} will be blocked for 5 minutes', { REASON: typeOfBlock }) }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="timeLeft">
                    {{ $t('Login.Too many login attempts have been made') }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="timeLeft" v-if="typeOfBlock === 'IP'">
                    {{ $t('Login.The IP address has been blocked for {MINUTES}:{SECONDS} minutes', { MINUTES: String(Math.floor(timeLeft / 60)).padStart(2, '0'), SECONDS: String(timeLeft % 60).padStart(2, '0') }) }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="timeLeft" v-if="typeOfBlock === 'username'">
                    {{ $t('Login.The username has been blocked for {MINUTES}:{SECONDS} minutes', { MINUTES: String(Math.floor(timeLeft / 60)).padStart(2, '0'), SECONDS: String(timeLeft % 60).padStart(2, '0') }) }}
                  </div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showLicenseError" v-html="$t('Login.You are not licensed for this product, contact your supplier for more information')"></div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showBlockedError" v-html="$t('Login.You are currently not allowed to sign in, contact your administrator')"></div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showSSOStateError" v-html="$t('Login.Single Sign On failed due to a configuration error or a timeout')"></div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showSSOIdError" v-html="$t('Login.Single Sign On failed, this account is already configured for SSO with a different user')"></div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showSSOSiteError" v-html="$t('Login.Single Sign On failed, user not attached to a site, contact your administrator')"></div>
                  <div class="form-row text-danger font-size-rem-9 justify-content-center text-center mb-1" v-show="showSSOMeetingError" v-html="$t('Login.Single Sign On meeting creation failed, contact your administrator')"></div>
                </div>
                <div class="d-flex py-3 pr-0 pr-md-5">
                  <button type="submit" class="btn btn-lg btn-block m-5 meek-btn" :class="{ disabled: loginButtonDisabled }" :disabled="loginButtonDisabled" v-if="loginButtonLoading === false" tabindex="5">{{ $t('Login.Sign in') }}</button>
                  <button type="submit" class="btn btn-lg btn-block m-5 disabled meek-btn" disabled="disabled" v-else><font-awesome-icon :icon="['fal', 'spinner']" spin role="presentation" /> {{ $t('Login.Signing in') }}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeekLogin',
  data () {
    return {
      username: '',
      password: '',
      loginButtonLoading: false,
      loginButtonDisabled: true,
      showError: false,
      attemptsLeft: false,
      typeOfBlock: false,
      timeLeft: false,
      timeLeftCountdown: false,
      forceLogout: false,
      showBlockedError: false,
      showForceLogout: false,
      showLicenseError: false,
      showProductError: false,
      mfaSecret: null,
      showSSOStateError: false,
      showSSOIdError: false,
      showSSOSiteError: false,
      showSSOMeetingError: false,
      state: null
    }
  },
  methods: {
    isValidProduct (product, hasFeature) {
      return (
        (product === 'direct' && (hasFeature('weseedo_direct') || hasFeature('weseedo_direct_2way'))) ||
        (product === 'personal' && hasFeature('weseedo_personal'))
      )
    },
    navigateUser (product, user, userSites) {
      const shouldShowPreview = userSites.some(site =>
        site.enabled_features.includes('preview_screen')
      )
      if (product === 'direct') {
        this.$router.push({ name: 'appointment' })
      } else if (product === 'personal') {
        if (user.role === 'assistent') {
          this.$router.push({ name: 'waiting_room' })
        } else {
          this.$router.push({ name: shouldShowPreview ? 'meeting' : 'waiting_room' })
        }
      }
    },
    handleLoginError () {
      this.$axios.logout(false)
      this.loginButtonLoading = false
      this.showLicenseError = true
    },
    processLogin () {
      const product = this.$store.getters.getProduct
      const user = this.$store.getters.getUser
      const hasFeature = this.$store.getters.userHasFeature
      const userSites = this.$store.getters.getUserSites
      if (this.isValidProduct(product, hasFeature)) {
        this.$webrtc.connect()
        this.loginButtonLoading = false
        this.$root.getNotificationBar()
        this.navigateUser(product, user, userSites)
      } else {
        this.handleLoginError()
      }
    },
    async validateLogin () {
      this.loginButtonLoading = true
      this.showError = false

      this.showBlockedError = false
      this.showForceLogout = false
      this.showLicenseError = false
      this.showProductError = false
      this.showSSOStateError = false
      this.showSSOIdError = false
      this.showSSOSiteError = false
      this.showSSOMeetingError = false
      this.attemptsLeft = false
      this.typeOfBlock = false
      this.timeLeft = false

      const auth = await this.$axios.login(this.username, this.password, this.forceLogout, this.state)
      console.log('AUTH DATA, SHOULD BE A REDIRECT: ')
      console.log(auth)
      if (auth.redirect) {
        console.log('REDIRECT TO: ', auth.redirect)
        window.location = auth.redirect
      } else if (typeof auth.status !== 'undefined' && auth.status === 200) {
        console.log('AUTH WAS SUCCESSFULL')
        this.processLogin()
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('ALREADY_LOGGED_IN_ELSEWHERE')) {
        this.showForceLogout = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('USER_BLOCKED')) {
        this.showBlockedError = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('LICENSE_ERROR')) {
        this.showLicenseError = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('SSO_WRONG_STATE')) {
        this.showSSOStateError = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('SSO_WRONG_ID')) {
        this.showSSOIdError = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('SSO_SITE_NOT_FOUND')) {
        this.showSSOSiteError = true
        this.loginButtonLoading = false
      } else if (typeof auth.data !== 'undefined' && typeof auth.data.error_code !== 'undefined' && auth.data.error_code.includes('SSO_MEETING_FAILED')) {
        this.showSSOMeetingError = true
        this.loginButtonLoading = false
      } else {
        if (auth.status === 401) {
          this.attemptsLeft = auth.data.result
          this.typeOfBlock = auth.data.error_message
        }
        if (auth.status === 429) {
          this.timeLeft = auth.data.result
          this.typeOfBlock = auth.data.error_message
          clearInterval(this.timeLeftCountdown)
          this.timeLeftCountdown = setInterval(() => {
            if (this.timeLeft) {
              this.timeLeft -= 1
            } else {
              this.timeLeft = false
              this.showError = false
              clearInterval(this.timeLeftCountdown)
            }
          }, 1000)
        }
        this.showError = true
        this.loginButtonLoading = false
      }
    }
  },
  computed: {
    watchUserPassInputs () {
      return [this.username, this.password]
    }
  },
  watch: {
    watchUserPassInputs () {
      this.loginButtonDisabled = !(this.username !== '' && this.password !== '')
    }
  },
  mounted () {
    this.$store.commit('setCamSettings', {})

    this.$root.getNotificationBar()

    this.$noSleep.disable()
    this.$webrtc.resetIsInitialConnection()
    if (this.$route.query.state) {
      this.state = this.$route.query.state
    }

    this.$refs.email.focus()
    this.$webrtc.disconnect(false)
  }
}
</script>

<style lang="scss" scoped>
.form-control {
  border-radius: 0px !important;
  border: 3px solid #e6e6e6 !important;
  box-shadow: none !important;
}
</style>
